
import { User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { SPECIAL_ADMIN_EMAILS } from "./constants";

// Check if user is an admin
export const checkIsAdmin = async (user: User | null): Promise<boolean> => {
  if (!user) return false;
  
  // Special admin list check - normalized email comparison
  if (user.email && SPECIAL_ADMIN_EMAILS.includes(user.email.toLowerCase())) {
    console.log("Special admin email matched:", user.email);
    return true;
  }
  
  try {
    const { data, error } = await supabase
      .from('user_roles')
      .select('role')
      .eq('user_id', user.id)
      .eq('role', 'admin')
      .single();
    
    if (error) {
      console.error("Error checking admin status:", error);
      return false;
    }
    
    return !!data;
  } catch (err) {
    console.error("Exception in checkIsAdmin:", err);
    return false;
  }
};

// Check if user is a sales assistant
export const checkIsSalesAssistant = async (user: User | null): Promise<boolean> => {
  if (!user) return false;
  
  try {
    const { data, error } = await supabase
      .from('user_roles')
      .select('role')
      .eq('user_id', user.id)
      .eq('role', 'sales_assistant')
      .single();
    
    if (error) {
      console.error("Error checking sales assistant status:", error);
      return false;
    }
    
    return !!data;
  } catch (err) {
    console.error("Exception in checkIsSalesAssistant:", err);
    return false;
  }
};

// Fetch assigned base IDs rather than names
export const fetchAssignedBases = async (user: User | null, isAdmin: boolean): Promise<string[]> => {
  if (!user) return ['all'];
  
  // Admins get access to all bases
  if (isAdmin) {
    return ['all'];
  }
  
  try {
    // Use the RPC function to get base IDs
    const { data: baseIds, error } = await supabase.rpc('get_user_accessible_base_ids');
    
    if (error) {
      console.error("Error fetching assigned base IDs:", error);
      
      // Fallback to direct query
      console.log("Falling back to direct query for base assignments");
      const { data: assignments, error: assignmentError } = await supabase
        .from('user_base_assignments')
        .select('base_name');
      
      if (assignmentError) {
        console.error("Error fetching base assignments:", assignmentError);
        return ['all'];
      }
      
      // Return base names if available, since there is no base_id column
      if (assignments && assignments.length > 0) {
        console.log("Assignments found:", assignments);
        return assignments.map(a => a.base_name).filter(Boolean);
      }
      
      return ['all'];
    }
    
    if (baseIds && baseIds.length > 0) {
      console.log("Base IDs from RPC:", baseIds);
      // Convert base IDs to strings for consistent handling
      return baseIds.map(id => id.toString());
    }
    
    console.log("No base IDs found, returning 'all'");
    return ['all'];
  } catch (err) {
    console.error("Exception in fetchAssignedBases:", err);
    return ['all'];
  }
};

// Sign out all sessions for the current user
export const signOutAllSessions = async (): Promise<void> => {
  try {
    // Check if we have a session before trying to sign out
    const { data } = await supabase.auth.getSession();
    
    if (!data.session) {
      console.log("No active session found, skipping global sign out");
      return;
    }
    
    // Using Supabase's signOut with scope: 'global' to sign out all sessions
    const { error } = await supabase.auth.signOut({ scope: 'global' });
    
    if (error) {
      console.error("Error signing out all sessions:", error);
      throw error;
    }
    
    console.log("Successfully signed out of all sessions");
  } catch (err) {
    console.error("Exception in signOutAllSessions:", err);
    throw err;
  }
};
